import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { prefixedKey } from '../../hooks/useTableUrlState.ts';
import { USER_STATUS_OPTIONS_BY_KEY, type UserStatusKey } from '../../lib/user-status.ts';
import { defaultUsersTableFilters, UNASSIGNED_CUSTOMER_FILTER } from './filter.ts';
import type {
  CustomerFilterValue,
  RoleStateKind,
  UsersTableFilters,
  UsersTableScope,
  UserTypeState,
} from './types.ts';

/**
 * The one URL prefix both mounts use, so `/users` and the customer detail page
 * write the same keys and neither collides with the customer page's Meters
 * table (which owns the unprefixed `page`/`pageSize`/`sortBy`/`sortDir`).
 *
 * Deliberately a constant and not a prop: making it configurable would invite
 * exactly the collision it exists to prevent.
 */
export const USERS_TABLE_URL_PREFIX = 'users';

/** `usersQ`, `usersCustomer`, ... — one naming rule, shared with `useTableUrlState`. */
const key = (base: string) => prefixedKey(USERS_TABLE_URL_PREFIX, base);

const SEARCH_KEY = key('q');
const CUSTOMER_KEY = key('customer');
const TYPE_KEY = key('type');
const STATUS_KEY = key('status');
const ROLE_KEY = key('role');
/** Owned by `useTableUrlState`; deleted here by the page-reset rule. */
const PAGE_KEY = key('page');

/** Every key this hook owns, in URL order. Used by `clear`. */
export const USERS_TABLE_FILTER_KEYS = [
  SEARCH_KEY,
  CUSTOMER_KEY,
  TYPE_KEY,
  STATUS_KEY,
  ROLE_KEY,
] as const;

const TYPE_VALUES: readonly UserTypeState[] = ['metron', 'external', 'unknown'];
const ROLE_VALUES: readonly RoleStateKind[] = [
  'admin',
  'propertyManager',
  'metronInternal',
  'notAssigned',
  'none',
  'unknown',
];

/**
 * Decoders are total: anything unparseable or unknown reads as "no filter"
 * rather than as a filter nothing can match, so a hand-edited or stale URL
 * shows users instead of an empty table.
 */
function decodeCustomer(raw: string | null): CustomerFilterValue | null {
  if (raw === null) return null;
  if (raw === UNASSIGNED_CUSTOMER_FILTER) return UNASSIGNED_CUSTOMER_FILTER;
  const id = Number(raw);
  return Number.isInteger(id) ? id : null;
}

function decodeType(raw: string | null): UserTypeState | null {
  return TYPE_VALUES.find((value) => value === raw) ?? null;
}

function decodeRole(raw: string | null): RoleStateKind | null {
  return ROLE_VALUES.find((value) => value === raw) ?? null;
}

function decodeStatus(raw: string | null): UserStatusKey | null {
  if (raw === null) return null;
  return raw in USER_STATUS_OPTIONS_BY_KEY ? raw as UserStatusKey : null;
}

/**
 * URL → filters. In `customer` mode a stale `usersCustomer` or `usersType` is
 * ignored: the route fixes the customer, and a customer's users are all
 * external.
 */
export function decodeUsersTableFilters(
  params: URLSearchParams,
  scope: UsersTableScope,
): UsersTableFilters {
  return {
    search: params.get(SEARCH_KEY) ?? '',
    customer: scope.mode === 'customer' ? null : decodeCustomer(params.get(CUSTOMER_KEY)),
    type: scope.mode === 'customer' ? null : decodeType(params.get(TYPE_KEY)),
    status: decodeStatus(params.get(STATUS_KEY)),
    role: decodeRole(params.get(ROLE_KEY)),
  };
}

/** filters → URL. Defaults are deleted, so a clean URL stays clean. */
function encodeUsersTableFilters(
  params: URLSearchParams,
  filters: UsersTableFilters,
  scope: UsersTableScope,
): void {
  const write = (paramKey: string, value: string | null) => {
    if (value === null || value === '') params.delete(paramKey);
    else params.set(paramKey, value);
  };

  write(SEARCH_KEY, filters.search.trim() === '' ? null : filters.search);
  // The customer is fixed by the route in `customer` mode, and a customer's
  // users are all external, so neither key is read nor written there.
  if (scope.mode !== 'customer') {
    write(CUSTOMER_KEY, filters.customer === null ? null : String(filters.customer));
    write(TYPE_KEY, filters.type);
  }
  write(STATUS_KEY, filters.status);
  write(ROLE_KEY, filters.role);
}

/**
 * A referentially stable copy of `scope`, so `{ mode: 'all' }` written inline
 * at a call site does not defeat the memoisation downstream.
 */
export function useStableScope(scope: UsersTableScope): UsersTableScope {
  const mode = scope.mode;
  const customerId = scope.mode === 'customer' ? scope.customerId : null;
  return useMemo<UsersTableScope>(
    () => (mode === 'customer' && customerId !== null ? { mode, customerId } : { mode: 'all' }),
    [mode, customerId],
  );
}

export interface UseUsersTableFiltersResult {
  filters: UsersTableFilters;
  /** Merges a patch into the current filters and resets the page. */
  setFilters: (patch: Partial<UsersTableFilters>) => void;
  /** Resets every filter **and** the page, but never sort or page size. */
  clear: () => void;
}

/**
 * The filter half of the users table's URL state, sharing
 * `useTableUrlState`'s key convention under the same `users` prefix.
 *
 * Two rules live here:
 *
 * 1. **Page reset.** Any filter or search write deletes `usersPage` in the
 *    *same* params mutation, so narrowing the list from page 3 can never leave
 *    the user stranded on a page that no longer exists.
 * 2. **Clear is filters + page only.** Sort and page size are view
 *    preferences, not filters, and survive Clear.
 *
 * Both this hook and `useTableUrlState` write through `setSearchParams`'s
 * functional updater with `{ replace: true }`, so a filter write and a
 * pagination write cannot clobber each other.
 */
export function useUsersTableFilters(scope: UsersTableScope): UseUsersTableFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();
  // Normalised to primitives first, so a caller passing a fresh object literal
  // every render does not invalidate every memo below.
  const stableScope = useStableScope(scope);

  const filters = useMemo(
    () => decodeUsersTableFilters(searchParams, stableScope),
    [searchParams, stableScope],
  );

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          // Rule 1: the page reset happens in the same write as the filter.
          next.delete(PAGE_KEY);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilters = useCallback(
    (patch: Partial<UsersTableFilters>) => {
      updateParams((params) => {
        encodeUsersTableFilters(
          params,
          { ...decodeUsersTableFilters(params, stableScope), ...patch },
          stableScope,
        );
      });
    },
    [updateParams, stableScope],
  );

  const clear = useCallback(() => {
    updateParams((params) => {
      for (const paramKey of USERS_TABLE_FILTER_KEYS) params.delete(paramKey);
    });
  }, [updateParams]);

  return { filters, setFilters, clear };
}

/** Re-exported so callers get defaults and the sentinel from one module. */
export { defaultUsersTableFilters, UNASSIGNED_CUSTOMER_FILTER };
